/**
 * SessionTimeoutWarning Component
 * Modal dialog that warns users when their session is about to expire
 *
 * Features:
 * - Countdown timer showing time remaining
 * - "Stay Signed In" button to extend session
 * - "Log Out" button to end session immediately
 * - Accessible with focus trap and screen reader announcements
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { useAuth } from '@/hooks/useAuth';
import { useAutoFocus } from '@/hooks/useFocusManagement';

/**
 * SessionTimeoutWarning - Session expiration warning modal
 *
 * Displays a warning dialog when the user's session is about to expire.
 * Shows a countdown timer and allows the user to extend their session
 * or log out immediately.
 *
 * @example
 * ```tsx
 * // Include in your App or AuthProvider
 * function App() {
 *   return (
 *     <>
 *       <Routes />
 *       <SessionTimeoutWarning />
 *     </>
 *   );
 * }
 * ```
 */
export const SessionTimeoutWarning: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showWarning, formattedTime, extendSession, isExtending } = useSessionTimeout();
  const staySignedInButtonRef = useAutoFocus<HTMLButtonElement>(showWarning);

  const { logout, isLogoutLoading } = useAuth();

  const handleLogout = async () => {
    await logout();
  };

  const handleExtendSession = async () => {
    await extendSession();
  };

  const isLoading = isExtending || isLogoutLoading;

  return (
    <Dialog
      open={showWarning}
      onClose={(_event, reason) => {
        // Block backdrop and escape dismissal — session extension
        // must come from the explicit "Stay Signed In" button
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
      aria-labelledby="session-timeout-title"
      aria-describedby="session-timeout-description"
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown
    >
      <DialogTitle id="session-timeout-title">Session Expiring</DialogTitle>

      <DialogContent>
        {/* Warning icon and countdown */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
          }}
        >
          <WarningAmberIcon color="warning" sx={{ fontSize: 32 }} aria-hidden="true" />
          <Typography variant="h6" component="p" aria-live="polite" aria-atomic="true">
            Your session will expire in{' '}
            <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
              {formattedTime}
            </Box>
          </Typography>
        </Box>

        {/* Description text */}
        <Typography id="session-timeout-description" color="text.secondary" sx={{ mb: 2 }}>
          To protect your account, you will be automatically logged out when the timer reaches zero.
        </Typography>

        {/* Call to action */}
        <Typography>Would you like to stay signed in?</Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleLogout} color="inherit" disabled={isLoading}>
          Log Out
        </Button>
        <Button
          onClick={handleExtendSession}
          variant="contained"
          disabled={isLoading}
          ref={staySignedInButtonRef}
          startIcon={isExtending ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isExtending ? 'Extending...' : 'Stay Signed In'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutWarning;
